import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Landmark,
  Newspaper,
  Calendar,
  Image,
  FileText,
  MessageSquare,
  LogOut,
  GraduationCap,
  Heart,
  CalendarCheck,
  Home,
  Briefcase,
  Building2,
  ScanLine,
  KeyRound,
  Loader2,
  BarChart3,
  Sparkles,
  ShieldCheck,
  Mail,
  MapPin,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showChangePw, setShowChangePw] = React.useState(false);
  const [pwForm, setPwForm] = React.useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    setPwLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword }),
      }).then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Failed');
        toast.success('Password changed successfully');
        setShowChangePw(false);
        setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      });
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const navLinks = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/members', label: 'Members', icon: Users },
    { to: '/admin/families', label: 'Families', icon: Home },
    { to: '/admin/family-reports', label: 'Family Dashboard', icon: BarChart3 },
    { to: '/admin/institutional-members', label: 'Institutions', icon: Building2 },
    { to: '/admin/professionals', label: 'Professionals', icon: Briefcase },
    { to: '/admin/professional-reports', label: 'Prof. Reports', icon: BarChart3 },
    { to: '/admin/conclave-registrations', label: 'Conclave', icon: CalendarCheck },
    { to: '/admin/scholarship-donors', label: 'Scholarship Donors', icon: Heart },
    { to: '/admin/scholarship-applications', label: 'Applications', icon: GraduationCap },
    { to: '/admin/ps-attendees', label: 'PS 2026 Attendees', icon: Heart },
    { to: '/admin/ps-non-attendees', label: 'PS 2026 Non-Attendees', icon: Heart },
    { to: '/admin/ps-reports', label: 'PS 2026 Reports', icon: BarChart3 },
    { to: '/admin/ps-checkin', label: 'PS Check-in', icon: ScanLine },
    { to: '/admin/ps-interest-activity', label: 'Interest Activity', icon: Heart },
    { to: '/admin/guest-volunteer', label: 'PS Guests/Volunteers', icon: Users },
    { to: '/admin/vision-convention', label: 'Vision Convention 2026', icon: Sparkles },
    { to: '/admin/send-email', label: 'Send Email', icon: Mail },
    { to: '/admin/custom-cities', label: 'Custom Cities', icon: MapPin },
    { to: '/admin/committees', label: 'Committees', icon: Landmark },
    { to: '/admin/news', label: 'News', icon: Newspaper },
    { to: '/admin/events', label: 'Events', icon: Calendar },
    { to: '/admin/gallery', label: 'Gallery', icon: Image },
    { to: '/admin/documents', label: 'Documents', icon: FileText },
    { to: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
  ];

  // Super-admin-only section
  if (user?.superAdmin) {
    navLinks.push({ to: '/admin/admin-users', label: 'Admin Users', icon: ShieldCheck });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border hidden lg:block">
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-border">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo.webp" 
                alt="Khatri Mahasabha Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="font-serif font-bold text-sm text-primary leading-tight">खत्री महासभा</h1>
                <p className="text-[10px] text-muted-foreground">Admin Panel</p>
              </div>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-auto">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg font-sans text-sm transition-colors ${
                    isActive(link.to)
                      ? 'bg-secondary text-secondary-foreground'
                      : 'hover:bg-muted text-foreground'
                  }`}
                  data-testid={`admin-nav-${link.label.toLowerCase()}`}
                >
                  <Icon size={18} />
                  <span>{link.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-border">
            <div className="mb-3 p-3 bg-muted rounded-lg">
              <p className="text-sm font-semibold text-foreground">{user?.fullName}</p>
              <p className="text-xs text-muted-foreground">Administrator</p>
            </div>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setShowChangePw(!showChangePw)}
                data-testid="admin-change-pw-btn"
              >
                <KeyRound size={14} className="mr-2" />
                Change Password
              </Button>
              {showChangePw && (
                <form onSubmit={handleChangePw} className="p-3 bg-muted/50 rounded-lg space-y-2" data-testid="admin-change-pw-form">
                  <Input type="password" placeholder="Current Password" value={pwForm.currentPassword} onChange={e => setPwForm(p => ({...p, currentPassword: e.target.value}))} required className="h-8 text-xs" data-testid="admin-pw-current" />
                  <Input type="password" placeholder="New Password (min 6)" value={pwForm.newPassword} onChange={e => setPwForm(p => ({...p, newPassword: e.target.value}))} required className="h-8 text-xs" data-testid="admin-pw-new" />
                  <Input type="password" placeholder="Confirm New Password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({...p, confirmPassword: e.target.value}))} required className="h-8 text-xs" data-testid="admin-pw-confirm" />
                  <Button type="submit" size="sm" disabled={pwLoading} className="w-full h-8 text-xs" data-testid="admin-pw-submit">
                    {pwLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Update Password
                  </Button>
                </form>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleLogout}
                data-testid="admin-logout-button"
              >
                <LogOut size={14} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-card border-b border-border px-6 py-4 lg:hidden">
          <div className="flex items-center justify-between">
            <h1 className="font-serif font-bold text-xl text-primary">Admin Panel</h1>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut size={16} />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-muted/30">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;